import React,{ useState, useEffect, useMemo } from 'react';
import { getList } from '../../../common/v4/BackendProvider';
import ReactApexChart from 'react-apexcharts';


const SmsChart = () => {
    const [data, setData] = useState([]);
    const [state, setState] = useState({series1: [], series2: [], series3: []});
    const [timeRange, setTimeRange] = useState({
        start: null,
        end: null
    });

    const fetchData = (startTime = null, endTime = null) => {
        let url = '/admin/dashboard/sms-chart';
        if (startTime && endTime) {
            url += `?start_time=${startTime}&end_time=${endTime}`;
        }
        
        getList(url).then(
            response => {
                setData(response.data.data);
                const chartData = response.data.data || [];
                
                const series1Data = chartData
                    .filter(item => item.timestamp) // Filter out items without timestamp
                    .map(item => {
                        const date = new Date(item.timestamp);
                        return isNaN(date.getTime()) ? null : [date.getTime(), item.mts];
                    })
                    .filter(item => item !== null); // Remove invalid entries
                    
                const series2Data = chartData
                    .filter(item => item.timestamp)
                    .map(item => {
                        const date = new Date(item.timestamp);
                        return isNaN(date.getTime()) ? null : [date.getTime(), item.mos];
                    })
                    .filter(item => item !== null);
                    
                const series3Data = chartData
                    .filter(item => item.timestamp)
                    .map(item => {
                        const date = new Date(item.timestamp);
                        return isNaN(date.getTime()) ? null : [date.getTime(), item.dlrs];
                    })
                    .filter(item => item !== null);
                
                setState({
                    series1: [{
                        name: 'MTs',
                        data: series1Data
                    }],             
                    series2: [{
                        name: 'MOs',
                        data: series2Data
                    }],
                    series3: [{
                        name: 'DLRs',
                        data: series3Data
                    }]
                });
            }
        ).catch(error => {
            console.error('Error fetching SMS chart data:', error);
        });
    };

    useEffect(() => {
        fetchData();
    }, []);


    const handleZoom = (chartContext, { xaxis }) => {
        if (xaxis.min && xaxis.max) {
            try {
                const startDate = new Date(xaxis.min);
                const endDate = new Date(xaxis.max);
                
                // Check if dates are valid
                if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                    console.warn('Invalid date values in zoom:', { min: xaxis.min, max: xaxis.max });
                    return;
                }
                
                const startTime = startDate.toISOString();
                const endTime = endDate.toISOString();
                
                setTimeRange({ start: startTime, end: endTime });
                fetchData(startTime, endTime);
            } catch (error) {
                console.error('Error handling zoom:', error);
            }
        }
    };

    const getOptions1 = () => ({
        title: {
            text: 'MTs',
            align: 'left',
            style: {
                fontSize: '14px'
            }
        },
        tooltip: {
            x: {
                format: 'yyyy-MM-dd HH'
            }
        },
        chart: {
            id: 'mts',
            group: 'sms',
            type: 'line',
            height: 80,
            events: {
                zoomed: function(chartContext, { xaxis }) {
                    handleZoom(chartContext, { xaxis });
                }
            }
        },
        colors: ['#008FFB'],
        xaxis: {
            type: 'datetime',
            format: 'HH:mm',
            tickAmount: 6,
        },
        yaxis: {
            labels: {
                formatter: function(value) {
                    return value.toFixed(0);
                },
            }
        }
    });

    const getOptions2 = () => ({
        title: {
            text: 'MOs',
            align: 'left',
            style: {
                fontSize: '14px'
            }
        },
        tooltip: {
            x: {
                format: 'yyyy-MM-dd HH'
            }
        },
        chart: {
            id: 'mos',
            group: 'sms',
            type: 'line',
            height: 80,
            events: {
                zoomed: function(chartContext, { xaxis }) {
                    handleZoom(chartContext, { xaxis });
                }
            }
        },
        colors: ['#00E396'],
        xaxis: {
            type: 'datetime',
            format: 'HH:mm',
            tickAmount: 6,
        },
        yaxis: {
            labels: {
                formatter: function(value) {
                    return value.toFixed(0);
                }
            }
        }
    });

    const getOptions3 = () => ({
        title: {
            text: 'DLRs',
            align: 'left',
            style: {
                fontSize: '14px'
            }
        },
        tooltip: {
            x: {
                format: 'yyyy-MM-dd HH'
            }
        },
        chart: {
            id: 'dlrs',
            group: 'sms', 
            type: 'line',
            height: 80,
            events: {
                zoomed: function(chartContext, { xaxis }) {
                    handleZoom(chartContext, { xaxis });
                }
            }
        },
        colors: ['#FEB019'],
        xaxis: {
            type: 'datetime',
            format: 'HH:mm',
            tickAmount: 6,
        },
        yaxis: {
            labels: {
                formatter: function(value) {
                    return value.toFixed(0);
                }
            }
        }
    });

    const options1 = useMemo(() => getOptions1(), []);
    const options2 = useMemo(() => getOptions2(), []);
    const options3 = useMemo(() => getOptions3(), []);

    // Create explicit series objects to ensure they're unique with proper null checks
    const mtSeries = useMemo(() => [{
        name: 'MTs',
        data: (state.series1 && state.series1[0] && state.series1[0].data) ? [...state.series1[0].data] : []
    }], [state.series1]);
    
    const moSeries = useMemo(() => [{
        name: 'MOs', 
        data: (state.series2 && state.series2[0] && state.series2[0].data) ? [...state.series2[0].data] : []
    }], [state.series2]);
    
    const dlrSeries = useMemo(() => [{
        name: 'DLRs',
        data: (state.series3 && state.series3[0] && state.series3[0].data) ? [...state.series3[0].data] : []
    }], [state.series3]);

    // Don't render charts until we have data
    if (!state.series1 || !state.series2 || !state.series3 || 
        !Array.isArray(state.series1) || !Array.isArray(state.series2) || !Array.isArray(state.series3) ||
        state.series1.length === 0 || state.series2.length === 0 || state.series3.length === 0) {
        return <div>Loading SMS chart data...</div>;
    }

    return (
        <div>
            <ReactApexChart
                type="line"
                height={160}
                series={mtSeries}
                options={options1}
            />
            <ReactApexChart
                type="line"
                height={160}
                series={moSeries}
                options={options2}
            />
            <ReactApexChart
                type="line"
                height={160}
                series={dlrSeries}
                options={options3}
            />
        </div>
    );
};

export default SmsChart;
